import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Euro, Calendar, User, FileText, Award, GraduationCap, Mail, Download, MoreVertical } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistrationsPage({ admin, onLogout }) {
  const [registrations, setRegistrations] = useState([]);
  const [trainings, setTrainings] = useState({});
  const [learners, setLearners] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [regsRes, trainingsRes, learnersRes] = await Promise.all([
        axios.get(`${API}/registrations`),
        axios.get(`${API}/trainings`),
        axios.get(`${API}/learners`)
      ]);

      setRegistrations(regsRes.data);
      
      // Create lookup maps
      const trainingsMap = {};
      trainingsRes.data.forEach(t => trainingsMap[t.id] = t);
      setTrainings(trainingsMap);

      const learnersMap = {};
      learnersRes.data.forEach(l => learnersMap[l.id] = l);
      setLearners(learnersMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (registrationId, docType, label) => {
    try {
      const response = await axios.get(`${API}/documents/${docType}/${registrationId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docType}_${registrationId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${label}:`, error);
      alert(`Erreur lors du téléchargement du document`);
    }
  };

  const updateStatus = async (registrationId, newStatus) => {
    try {
      await axios.put(`${API}/registrations/${registrationId}`, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const deleteRegistration = async (registrationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette inscription ?')) return;
    try {
      await axios.delete(`${API}/registrations/${registrationId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'En attente' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Confirmé' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Annulé' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Terminé' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text} flex items-center gap-1 w-fit`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'En attente' },
      paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'Payé' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Remboursé' }
    };

    const badge = badges[status] || badges.pending;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const filteredRegistrations = registrations.filter(reg => 
    filterStatus === 'all' || reg.status === filterStatus
  );

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Gestion des Inscriptions">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100">
            <p className="text-sm text-gray-600 mb-1">Total</p>
            <p className="text-3xl font-bold text-violet-600" data-testid="stat-total-registrations">
              {registrations.length}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-green-100">
            <p className="text-sm text-gray-600 mb-1">Confirmées</p>
            <p className="text-3xl font-bold text-green-600" data-testid="stat-confirmed">
              {registrations.filter(r => r.status === 'confirmed').length}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-yellow-100">
            <p className="text-sm text-gray-600 mb-1">En attente</p>
            <p className="text-3xl font-bold text-yellow-600" data-testid="stat-pending">
              {registrations.filter(r => r.status === 'pending').length}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
            <p className="text-sm text-gray-600 mb-1">Revenu total</p>
            <p className="text-3xl font-bold text-blue-600" data-testid="stat-revenue">
              {registrations.filter(r => r.payment_status === 'paid')
                .reduce((sum, r) => sum + r.amount_paid, 0)
                .toFixed(2)} €
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
            data-testid="filter-registration-status"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmé</option>
            <option value="cancelled">Annulé</option>
            <option value="completed">Terminé</option>
          </select>
        </div>

        {/* Registrations List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune inscription</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRegistrations.map((registration) => {
              const training = trainings[registration.training_id];
              const learner = learners[registration.learner_id];

              return (
                <div
                  key={registration.id}
                  className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow"
                  data-testid={`registration-card-${registration.id}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Learner Info */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Apprenant</p>
                      {learner ? (
                        <div>
                          <p className="font-semibold text-gray-800">
                            {learner.first_name} {learner.last_name}
                          </p>
                          <p className="text-sm text-gray-600">{learner.email}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">Non trouvé</p>
                      )}
                    </div>

                    {/* Training Info */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Formation</p>
                      {training ? (
                        <div>
                          <p className="font-semibold text-gray-800">{training.title}</p>
                          <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                            <Calendar size={14} />
                            {training.start_date 
                              ? new Date(training.start_date).toLocaleDateString('fr-FR')
                              : 'Date non définie'}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">Non trouvée</p>
                      )}
                    </div>

                    {/* Status & Payment */}
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Statuts</p>
                      <div className="space-y-2">
                        {getStatusBadge(registration.status)}
                        {getPaymentBadge(registration.payment_status)}
                      </div>
                    </div>

                    {/* Amount & Actions */}
                    <div className="flex flex-col justify-between">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Montant</p>
                        <div className="flex items-center gap-1 text-lg font-bold text-violet-600">
                          <Euro size={18} />
                          {registration.amount_paid.toFixed(2)}
                        </div>
                        {registration.payment_method && (
                          <p className="text-xs text-gray-500 mt-1">
                            via {{ card: 'carte', stripe: 'carte', bank_transfer: 'virement' }[registration.payment_method] || registration.payment_method}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {registration.notes && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Notes</p>
                      <p className="text-sm text-gray-600">{registration.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex flex-wrap gap-2 items-center">
                      <p className="text-xs text-gray-500 mr-2 w-full sm:w-auto">Documents:</p>
                      <button
                        onClick={() => downloadDocument(registration.id, 'invoice', 'Facture')}
                        className="flex items-center gap-1 px-3 py-1.5 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors text-xs"
                        data-testid={`download-invoice-${registration.id}`}
                      >
                        <FileText size={14} />
                        Facture
                      </button>
                      <button
                        onClick={() => downloadDocument(registration.id, 'convocation', 'Convocation')}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs"
                        data-testid={`download-convocation-${registration.id}`}
                      >
                        <Mail size={14} />
                        Convocation
                      </button>
                      <button
                        onClick={() => downloadDocument(registration.id, 'attestation', 'Attestation de présence')}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-xs"
                        data-testid={`download-attestation-${registration.id}`}
                      >
                        <Award size={14} />
                        Attest. présence
                      </button>
                      <button
                        onClick={() => downloadDocument(registration.id, 'certificate', 'Attestation de fin de formation')}
                        className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors text-xs"
                        data-testid={`download-certificate-${registration.id}`}
                      >
                        <GraduationCap size={14} />
                        Attest. fin
                      </button>

                      <div className="ml-auto flex gap-2">
                        <select
                          value={registration.status}
                          onChange={(e) => updateStatus(registration.id, e.target.value)}
                          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                          data-testid={`status-select-${registration.id}`}
                        >
                          <option value="pending">En attente</option>
                          <option value="confirmed">Confirmé</option>
                          <option value="completed">Terminé</option>
                          <option value="cancelled">Annulé</option>
                        </select>
                        <button
                          onClick={() => deleteRegistration(registration.id)}
                          className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-xs"
                          data-testid={`delete-registration-${registration.id}`}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}